import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, ShieldCheck, User, Clock, Check, Package, BarChart3, AlertTriangle, Star } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { getLocalizedLocationLabel } from '../components/AdInfoCard';
import { guardFirstImage } from '../lib/imageTrust';
import { toAbsoluteUrl } from '../lib/media';
import WasslaImage from '../components/common/WasslaImage';
import { formatPrice } from '../utils/currency';
import { formatMemberSince } from '../utils/dataConsistency';
import { computeProfileStrength, getProfileStrengthItems, getProfileStrengthLabel } from '../utils/profileStrength';
import { SellerRatingBadge, SellerReviewsSheet, LeaveReviewModal } from '../components/SellerReviews';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import './SellerProfile.css';

const API = process.env.REACT_APP_BACKEND_URL;

function getLastActiveLabel(lastLoginAt, t) {
  if (!lastLoginAt) return null;
  try {
    const last = new Date(lastLoginAt);
    const now = new Date();
    const diffMs = now - last;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays < 1) return t('adDetails.activeToday');
    if (diffDays < 7) return t('adDetails.activeThisWeek');
    if (diffDays < 30) return t('adDetails.activeRecently');
    return null;
  } catch { return null; }
}

function getCompletenessScore(seller) {
  // Delegates to the shared profile-strength helper so Seller Profile, Ad Detail,
  // and every other surface compute the exact same score from the same fields.
  return computeProfileStrength(seller);
}

export default function SellerProfile() {
  const { userId } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isArabic = i18n.language?.startsWith('ar');
  const locale = isArabic ? 'ar' : 'en';

  const [seller, setSeller] = useState(null);
  const [ads, setAds] = useState([]);
  const [adsTotal, setAdsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [responseHint, setResponseHint] = useState(null);
  // iter317 — Reviews sheet + leave-review modal.
  const [showReviewsSheet, setShowReviewsSheet] = useState(false);
  const [showLeaveReviewModal, setShowLeaveReviewModal] = useState(false);
  const isSelf = currentUser?.user_id && userId && String(currentUser.user_id) === String(userId);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setAvatarFailed(false);
    Promise.all([
      fetch(`${API}/api/seller/${userId}`).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/seller/${userId}/ads?limit=200`).then(r => r.ok ? r.json() : { ads: [], total: 0 }),
      fetch(`${API}/api/seller/${userId}/response-score`).then(r => r.ok ? r.json() : null),
    ]).then(([profile, adsData, respScore]) => {
      setSeller(profile);
      setAds(adsData.ads || []);
      setAdsTotal(adsData.total ?? (adsData.ads || []).length);
      if (respScore && respScore.score >= 40) {
        const key = respScore.score >= 80 ? 'adDetails.responseVeryFast'
          : respScore.score >= 60 ? 'adDetails.responseFast'
          : 'adDetails.responseHours';
        setResponseHint(key);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [userId]);

  const handleLoadMore = () => {
    if (loadingMore || ads.length >= adsTotal) return;
    setLoadingMore(true);
    fetch(`${API}/api/seller/${userId}/ads?limit=200&skip=${ads.length}`)
      .then(r => r.ok ? r.json() : { ads: [] })
      .then(data => {
        setAds(prev => [...prev, ...(data.ads || [])]);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  if (loading) {
    return (
      <div className="seller-profile-page" data-testid="seller-profile-page">
        <AppHeader
          title={t('adDetails.seller')}
          backFallback="/shop"
          testId="seller-profile-header"
        />
        <div className="sp-loading">{t('loading')}</div>
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="seller-profile-page" data-testid="seller-profile-page">
        <AppHeader
          title={t('adDetails.seller')}
          backFallback="/shop"
          testId="seller-profile-header"
        />
        <div className="sp-empty">{t('adDetails.adNotFound')}</div>
      </div>
    );
  }

  const completeness = getCompletenessScore(seller);
  const activeLabel = getLastActiveLabel(seller.last_login_at, t);
  const cityLabel = getLocalizedLocationLabel(seller, locale);
  const joinedDate = formatMemberSince(seller.joined_at, locale);

  return (
    <div className="seller-profile-page" data-testid="seller-profile-page">
      <AppHeader
        title={t('adDetails.seller')}
        backFallback="/shop"
        testId="seller-profile-header"
      />

      {/* Seller Card */}
      <div className="sp-card" data-testid="seller-card">
        <div className="sp-avatar-row">
          {seller.avatar && !avatarFailed ? (
            <img
              src={toAbsoluteUrl(seller.avatar)}
              alt=""
              className="sp-avatar"
              onError={() => setAvatarFailed(true)}
              data-testid="seller-avatar-img"
            />
          ) : (
            <div className="sp-avatar sp-avatar-fallback" data-testid="seller-avatar-fallback">
              {seller.name ? (
                <span className="sp-avatar-initial">{seller.name.trim().charAt(0).toUpperCase()}</span>
              ) : (
                <User size={28} />
              )}
            </div>
          )}
          <div className="sp-info">
            <div className="sp-name">
              {seller.name || t('adDetails.seller')}
              {seller.is_verified && <ShieldCheck size={16} className="sp-verified-icon" />}
            </div>
            {cityLabel && (
              <div className="sp-meta"><MapPin size={13} /> {cityLabel}</div>
            )}
            {joinedDate && (
              <div className="sp-meta"><Clock size={13} /> {t('adDetails.joinedOn')} {joinedDate}</div>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="sp-stats" data-testid="seller-stats">
          <div className="sp-stat">
            <span className="sp-stat-num" data-testid="seller-active-count">{adsTotal}</span>
            <span className="sp-stat-label">{t('adDetails.activeListings')}</span>
          </div>
          <div className="sp-stat">
            <span className="sp-stat-num">{seller.total_views || 0}</span>
            <span className="sp-stat-label">{t('account.totalViews')}</span>
          </div>
          {activeLabel && (
            <div className="sp-stat sp-stat-active" data-testid="seller-last-active">
              <span className="sp-active-dot"></span>
              <span className="sp-stat-label">{activeLabel}</span>
            </div>
          )}
        </div>

        {/* Seller Trust Layer */}
        {(() => {
          const hasPhone = !!seller.phone || seller.is_verified;
          const hasEmail = seller.is_verified;
          let accountAgeDays = null;
          let showNewWarning = false;
          let showCautionWarning = false;
          if (seller.joined_at) {
            try {
              const joined = new Date(seller.joined_at);
              accountAgeDays = Math.floor((Date.now() - joined.getTime()) / 86400000);
              if (accountAgeDays < 7) showNewWarning = true;
              if (accountAgeDays < 3) showCautionWarning = true;
            } catch {}
          }
          const totalAds = seller.stats?.total_ads_count || adsTotal;

          return (
            <div className="sp-trust-layer" data-testid="sp-trust-layer">
              {/* Anti-scam warnings */}
              {showCautionWarning && (
                <div className="sp-caution" data-testid="sp-caution-warning">
                  <AlertTriangle size={14} />
                  <span>{isArabic ? 'بائع جديد — تعامل بحذر' : 'New seller — trade carefully'}</span>
                </div>
              )}
              {showNewWarning && !showCautionWarning && (
                <div className="sp-new-badge" data-testid="sp-new-warning">
                  <Clock size={12} />
                  <span>{isArabic ? 'حساب بائع جديد' : 'New seller account'}</span>
                </div>
              )}

              {/* 1. Member since */}
              {joinedDate && (
                <div className="sp-trust-row" data-testid="sp-member-since">
                  <Clock size={13} className="sp-trust-icon" />
                  <span>{t('adDetails.memberSince')} {joinedDate}</span>
                </div>
              )}

              {/* 2. Verification badges */}
              <div className="sp-trust-badges">
                {seller.is_verified && (
                  <span className="sp-tbadge sp-tbadge-verified" data-testid="sp-verified-badge">
                    <ShieldCheck size={12} /> {isArabic ? 'موثق' : 'Verified'}
                  </span>
                )}
                {hasPhone && (
                  <span className="sp-tbadge sp-tbadge-ok" data-testid="sp-phone-badge">
                    <Check size={11} /> {isArabic ? 'هاتف موثق' : 'Phone verified'}
                  </span>
                )}
                {hasEmail && (
                  <span className="sp-tbadge sp-tbadge-ok" data-testid="sp-email-badge">
                    <Check size={11} /> {isArabic ? 'بريد إلكتروني موثق' : 'Email verified'}
                  </span>
                )}
                {/* Responsiveness badge — backend gates on ≥3 contact_clicks
                    AND a label string is produced. We only render when
                    `response_enough_data === true` so new sellers never
                    get a misleading badge. */}
                {seller.response_enough_data && (isArabic ? seller.response_label_ar : seller.response_label_en) && (
                  <span
                    className="sp-tbadge sp-tbadge-ok"
                    data-testid="sp-response-badge"
                    title={`Response score ${seller.response_score}/100`}
                  >
                    <Clock size={11} />{' '}
                    {isArabic ? seller.response_label_ar : seller.response_label_en}
                  </span>
                )}
              </div>

              {/* 3. Active listings */}
              <div className="sp-trust-row" data-testid="sp-active-listings">
                <Package size={13} className="sp-trust-icon" />
                <span>{adsTotal} {t('adDetails.activeListings')}</span>
              </div>

              {/* 4. Total listings */}
              {totalAds > 0 && (
                <div className="sp-trust-row sp-trust-muted" data-testid="sp-total-listings">
                  <BarChart3 size={13} className="sp-trust-icon" />
                  <span>{totalAds} {isArabic ? 'إجمالي الإعلانات' : 'total listings'}</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Response Hint */}
        {responseHint && (
          <div className="sp-response-hint" data-testid="seller-response-hint">
            <Clock size={13} />
            <span>{t(responseHint)}</span>
          </div>
        )}

        {/* iter317 — Rating badge + Rate seller CTA.
            Badge is always visible (renders "No reviews yet" when empty).
            CTA is hidden when the viewer is the seller themselves. */}
        <div className="sp-reviews-row" data-testid="sp-reviews-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 10 }}>
          <SellerRatingBadge
            sellerUserId={userId}
            onOpen={() => setShowReviewsSheet(true)}
          />
          {!isSelf && (
            <button
              type="button"
              className="rate-seller-cta"
              onClick={() => setShowLeaveReviewModal(true)}
              data-testid="rate-seller-cta"
              style={{ marginTop: 0, width: 'auto', flex: '1 1 160px' }}
            >
              <Star size={14} />
              <span>{t('reviews.rateSellerCta')}</span>
            </button>
          )}
        </div>

        {/* Profile Completeness */}
        <div className="sp-completeness" data-testid="seller-completeness">
          <div className="sp-completeness-header">
            <span>{t('adDetails.profileCompleteness')}</span>
            <span className="sp-completeness-pct">{completeness}%</span>
          </div>
          <div className="sp-completeness-bar">
            <div className="sp-completeness-fill" style={{ width: `${completeness}%` }}></div>
          </div>
          {/* Checklist: only shown when profile isn't 100% yet.
              "Complete your profile to increase trust" — helps sellers know
              exactly what to add to raise their score. */}
          {completeness < 100 && (() => {
            const missing = getProfileStrengthItems(seller).filter(i => !i.passed);
            if (missing.length === 0) return null;
            return (
              <div className="sp-completeness-checklist" data-testid="seller-completeness-checklist">
                <div className="sp-completeness-checklist-title">
                  {isArabic ? 'أكمل ملفك لزيادة الثقة' : 'Complete your profile to increase trust'}
                </div>
                <ul className="sp-completeness-checklist-items">
                  {missing.slice(0, 4).map(item => (
                    <li key={item.key} data-testid={`seller-checklist-${item.key}`}>
                      <span className="sp-checklist-dot" aria-hidden="true">+{item.weight}%</span>
                      <span>{getProfileStrengthLabel(item.key, isArabic)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Listings */}
      <div className="sp-listings-header">
        <span>{t('adDetails.sellerListings')}</span>
        <span className="sp-listings-count">{adsTotal}</span>
      </div>

      {ads.length === 0 ? (
        <div className="sp-empty">{t('adDetails.noListings')}</div>
      ) : (
        <>
          <div className="sp-grid" data-testid="seller-listings-grid">
            {ads.map(ad => (
              <button
                key={ad.ad_id}
                className="sp-ad-card"
                onClick={() => navigate(ad.is_book_listing ? `/student-hub/books/${ad.ad_id}` : `/ad/${ad.ad_id}`)}
                data-testid={`seller-ad-${ad.ad_id}`}
              >
                <div className="sp-ad-img">
                  <WasslaImage
                    src={guardFirstImage(ad.images, ad.category || ad.category_id)}
                    alt=""
                    fallbackType="ad"
                    testid={`seller-ad-image-${ad.ad_id}`}
                    showCaption={false}
                  />
                </div>
                <div className="sp-ad-body">
                  <div className="sp-ad-title">{ad.title}</div>
                  <div className="sp-ad-price">
                    {ad.price
                      ? formatPrice(ad.price, ad.currency || 'SDG', locale)
                      : t('adDetails.priceOnRequest')}
                  </div>
                  {(ad.city || ad.location) && (
                    <div className="sp-ad-loc">{getLocalizedLocationLabel(ad, locale)}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
          {ads.length < adsTotal && (
            <button
              className="sp-load-more"
              data-testid="seller-load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore
                ? t('loading')
                : `${t('adDetails.showMore')} (${ads.length} / ${adsTotal})`}
            </button>
          )}
        </>
      )}

      {/* iter317 — Reviews sheet + Leave review modal */}
      <SellerReviewsSheet
        sellerUserId={userId}
        open={showReviewsSheet}
        onClose={() => setShowReviewsSheet(false)}
        onLeaveReview={!isSelf ? () => {
          setShowReviewsSheet(false);
          setShowLeaveReviewModal(true);
        } : null}
      />
      <LeaveReviewModal
        sellerUserId={userId}
        open={showLeaveReviewModal}
        onClose={() => setShowLeaveReviewModal(false)}
        onSubmitted={() => {
          setShowLeaveReviewModal(false);
          toast.success(isArabic ? 'تم إرسال تقييمك. شكراً!' : 'Thanks — your review was submitted.');
        }}
      />
    </div>
  );
}
